import { of as observableOf, Observable } from 'rxjs';

import { map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material';
import {
  LoopBackAuth,
  OrganisationApi,
  OrganisationInterface,
  OrganisationResponseInterface,
  OrganisationsApi
} from '@careapp/angular-sdk';
import {
  ADMIN_ROLE,
  DASHBOARD_ROLES,
  highestRole,
  ORGANISATION_ROLE_HEIRARCHY
} from '@careapp/core';
import { Actions, Effect } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { CAAction, Organisations, OrgContextChange } from '../actions';
import * as selectors from '../reducers';

@Injectable()
export class OrganisationEffects {
  @Effect()
  findUserOrganisations = this.actions$.ofType(Organisations.REQUEST_OWN).pipe(
    withLatestFrom(this.store.select(selectors.auth.isAdmin)),
    switchMap(([action, isAdmin]) =>
      this.api
        .listOrganisations()
        .map((payload: OrganisationResponseInterface[]) => {
          if (!isAdmin) {
            payload = payload.filter(value =>
              DASHBOARD_ROLES.includes(
                highestRole(value.roles, [ADMIN_ROLE, ...ORGANISATION_ROLE_HEIRARCHY])
              )
            );
          }
          return Organisations.ResponseSuccessOwn(payload);
        })
        .catch(err => {
          return observableOf(Organisations.ResponseFailOwn(err));
        })
    )
  );

  @Effect()
  saveOrganisation = this.actions$.ofType(Organisations.REQUEST_SAVE).pipe(
    switchMap(action =>
      this.adminApi
        .updateAttributes(action.payload.id, action.payload.data)
        .map((payload: OrganisationResponseInterface) => {
          return Organisations.ResponseSuccessSaveAction(payload);
        })
        .catch(err => {
          return observableOf(Organisations.ResponseFailSaveAction(err));
        })
    )
  );

  @Effect()
  getOrganisations = this.actions$.ofType(Organisations.REQUEST_ALL).pipe(
    switchMap(action => {
      return this.adminApi
        .find()
        .map((payload: any) => {
          return Organisations.ResponseSuccessAllAction(payload);
        })
        .catch(err => {
          return observableOf(Organisations.ResponseFailAllAction(err));
        });
    })
  );

  @Effect()
  organisationDetail = this.actions$.ofType(Organisations.REQUEST_BY_ID).pipe(
    switchMap((action: CAAction) => {
      return this.adminApi
        .findById(action.payload.id)
        .map((payload: OrganisationInterface) => {
          return Organisations.ResponseSuccessByIdAction(payload);
        })
        .catch(err => {
          return observableOf(Organisations.ResponseFailByIdAction(err));
        });
    })
  );

  @Effect()
  createOrganisation = this.actions$.ofType(Organisations.REQUEST_CREATE).pipe(
    switchMap((action: CAAction) => {
      return this.adminApi
        .create(action.payload)
        .map((response: any) => {
          return Organisations.ResponseSuccessCreateAction(response);
        })
        .catch(err => {
          return observableOf(Organisations.ResponseFailCreateAction(err));
        });
    })
  );

  @Effect()
  refreshOrgsAfterCreate = this.actions$
    .ofType(Organisations.RESPONSE_SUCCESS_CREATE)
    .pipe(map(action => Organisations.RequestOwn()));

  @Effect()
  getPreferences = this.actions$.ofType(Organisations.REQUEST_PREFERENCES).pipe(
    withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
    switchMap(([action, id]) => {
      return this.api
        .getOrganisationPreference(id)
        .map(res => Organisations.ResponseSuccessPreferences({ ...res, organisationId: id }))
        .catch(err => observableOf(Organisations.ResponseFailPreferences(err)));
    })
  );

  @Effect()
  updatePreferences = this.actions$.ofType(Organisations.UPDATE_PREFERENCES).pipe(
    withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
    mergeMap(([action, id]) => {
      return this.api
        .setOrganisationPreference(id, action.payload)
        .map(res => Organisations.SuccessUpdatePreferences({ ...res, organisationId: id }))
        .catch(err => observableOf(Organisations.FailUpdatePreferences(err)));
    })
  );

  @Effect()
  getBranding = this.actions$.ofType(Organisations.REQUEST_BRANDING).pipe(
    withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
    switchMap(([action, id]) => {
      return this.api
        .getBranding(id)
        .map(res => Organisations.ResponseSuccessBranding({ ...res, organisationId: id }))
        .catch(err => observableOf(Organisations.ResponseFailBranding(err)));
    })
  );

  @Effect()
  updateBrandingColors = this.actions$.ofType(Organisations.UPDATE_BRANDING_COLORS).pipe(
    withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
    switchMap(([action, id]) => {
      return this.api
        .updateBrandingColors(id, action.payload)
        .map(res => Organisations.SuccessUpdateBrandingColors({ ...res, organisationId: id }))
        .catch(err => observableOf(Organisations.FailUpdateBrandingColors(err)));
    })
  );

  @Effect()
  resetBrandingColors = this.actions$.ofType(Organisations.RESET_BRANDING_COLORS).pipe(
    withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
    switchMap(([action, id]) =>
      this.dialog
        .open(ConfirmationDialogComponent, {
          data: {
            title: 'Reset Colors?',
            body:
              'Are you sure you want to reset your branding colors to the app defaults? This can not be undone',
            confirm: 'Reset',
            cancel: 'Cancel',
            confirmColor: 'warn'
          }
        })
        .afterClosed()
        .pipe(map(result => [action, id, result]))
    ),
    switchMap(
      ([action, id, result]) =>
        result
          ? this.api
              .updateBrandingColors(id, action.payload)
              .map(res => Organisations.SuccessUpdateBrandingColors({ ...res, organisationId: id }))
              .catch(err => observableOf(Organisations.FailUpdateBrandingColors(err)))
          : observableOf({ type: 'CANCELLED' })
    )
  );

  @Effect()
  removeBrandingImage = this.actions$.ofType(Organisations.REMOVE_BRANDING_IMAGE).pipe(
    withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
    switchMap(([action, id]) =>
      this.dialog
        .open(ConfirmationDialogComponent, {
          data: {
            title: 'Reset Image?',
            body:
              'Are you sure you want to reset this image to the application default? This can not be undone',
            confirm: 'Reset',
            cancel: 'Cancel',
            confirmColor: 'warn'
          }
        })
        .afterClosed()
        .pipe(map(result => [action, id, result]))
    ),
    switchMap(
      ([action, id, result]) =>
        result
          ? this.api
              .updateBrandingMedia(id, { ...action.payload, purpose: null })
              .map(res => Organisations.SuccessUpdateBrandingImage({ ...res, organisationId: id }))
              .catch(err => observableOf(Organisations.FailUpdateBrandingImage(err)))
          : observableOf({ type: 'CANCELLED' })
    )
  );

  @Effect()
  updateBrandingMedia = this.actions$.ofType(Organisations.UPDATE_BRANDING_IMAGE).pipe(
    withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
    switchMap(([action, id]) => {
      return this.api
        .updateBrandingMedia(id, action.payload)
        .map(res => Organisations.SuccessUpdateBrandingImage({ ...res, organisationId: id }))
        .catch(err => observableOf(Organisations.FailUpdateBrandingImage(err)));
    })
  );

  @Effect()
  contextSwitch = this.actions$
    .ofType(Organisations.SELECT_BY_ID)
    .pipe(map(() => OrgContextChange()));

  constructor(
    private store: Store<any>,
    private actions$: Actions<CAAction>,
    private api: OrganisationsApi,
    private adminApi: OrganisationApi,
    private auth: LoopBackAuth,
    private dialog: MatDialog
  ) {}
}
